import React from "react";
import { IconButton, Layout } from "@evertrue/et-components";
import { useChatNavigationContext } from "../contexts/chat-navigation-context";
import { useChatCurrentUser } from "../contexts/chat-current-user-context";
import ChatLink from "./chat-room-link";
import Header from "./header";
import { useCachedPoolsByIds } from "../contexts/cached-pools-context";
import {
  searchCompare,
  getPoolIdFromRoomName,
  parseMessage,
  getVMSPoolIdListFromRooms,
  getUserIdFromRoomName,
} from "../utils/utils";
import { useChatRoomSubscription } from "../contexts/chat-room-subscription-context";
import ChatActionBar from "./chat-action-bar";
import { useChatDependencyContext } from "../contexts/chat-dependency-context";
import useAllETUsersByIdsResolver from "../hooks/use-all-et-users-by-ids-resolver";
import ChatHomeEmptyState from "./chat-home-empty-state";

// This component should search the existing chats for a user/pool name
const ChatHome = () => {
  const [nav_state, dispatch] = useChatNavigationContext();
  const {
    currentChatUser: { rooms = [] },
  } = useChatCurrentUser();
  const [search_val, setSearchVal] = React.useState("");
  const { messages } = useChatRoomSubscription();
  const { oid, userId, mixPanelEvents } = useChatDependencyContext();

  // kicks off initial fetch(es) for all ET users
  const { allUsersById } = useAllETUsersByIdsResolver();

  const pool_ids = getVMSPoolIdListFromRooms(oid, rooms);
  const { allPoolsById } = useCachedPoolsByIds(pool_ids);

  const [unread_rooms, read_rooms] = rooms.reduce(
    (acc, room) => {
      const pool_id = getPoolIdFromRoomName(oid, room.name);
      const user_id = getUserIdFromRoomName(oid, room.name, userId);

      const user = allUsersById[user_id] || {};
      // look up pool
      const pool = allPoolsById[pool_id] || {};
      if (!user.id && !pool.id) return acc;
      // if it's not a pool chat the name will be undefined which means it's a dm
      const name_to_compare = pool.name || user.name;

      const message = messages[room.id];
      // filter based on search input val
      if (searchCompare(name_to_compare, search_val)) {
        // group into read and unread
        if (room.unreadCount && room.unreadCount > 0) {
          acc[0].push({ room, pool, user, message });
        } else {
          acc[1].push({ room, pool, user, message });
        }
      }
      return acc;
    },
    [[], []]
  );

  return (
    <Layout
      header={
        <>
          <Header title="Chat" showClose />
          <ChatActionBar debounceTimeout={1} searchText={search_val} setSearchText={setSearchVal}>
            <IconButton
              title="start a chat"
              onClick={() => {
                mixPanelEvents.globalPlusAdd();
                dispatch({ type: "NAVIGATE_TO_LIST" });
              }}
              className="chat-home--add-button"
              icon="add"
              size={2}
            />
          </ChatActionBar>
          {nav_state.left_room_message && (
            <div className="chat-home--banner">
              <div className="chat-home--banner-message">
                Left <strong>{" " + nav_state.left_room_message}</strong>
              </div>
              <IconButton
                size={2}
                icon="close"
                className="chat-home--banner-close"
                onClick={() => dispatch({ type: "UPDATE_LEFT_ROOM_MESSAGE", payload: null })}
              />
            </div>
          )}
        </>
      }
    >
      {rooms.length ? (
        <div>
          {Boolean(unread_rooms.length) && (
            <>
              <div className="chat-home--message-section">Unreads</div>
              {unread_rooms.map(({ room, pool, user, message }) => {
                return (
                  <ChatLink
                    key={room.id}
                    name={pool.name}
                    userId={user.id}
                    room={room}
                    poolId={pool.id}
                    className="chat-home--link chat-home--unread"
                  >
                    {message && parseMessage(message).text}
                  </ChatLink>
                );
              })}
            </>
          )}
          {Boolean(read_rooms.length) && (
            <>
              <div className="chat-home--message-section">Read</div>
              {read_rooms.map(({ room, pool, user, message }) => {
                return (
                  <ChatLink
                    key={room.id}
                    name={pool.name}
                    userId={user.id}
                    room={room}
                    poolId={pool.id}
                    className="chat-home--link chat-home--read"
                  >
                    {message && parseMessage(message).text}
                  </ChatLink>
                );
              })}
            </>
          )}
        </div>
      ) : (
        <ChatHomeEmptyState />
      )}
    </Layout>
  );
};

ChatHome.propTypes = {};

export default ChatHome;
